import { motion } from "motion/react";
import { TrendingUp, Award, Target, Trophy } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const performanceData = [
  { year: "2010", meanScore: 351.26 },
  { year: "2011", meanScore: 338.96 },
  { year: "2012", meanScore: 350.43 },
  { year: "2013", meanScore: 338.0 },
  { year: "2014", meanScore: 367.78 },
  { year: "2015", meanScore: 381.15 },
  { year: "2016", meanScore: 377.5 },
  { year: "2017", meanScore: 381.27 },
  { year: "2018", meanScore: 373.2 },
  { year: "2019", meanScore: 385.16 },
  { year: "2020", meanScore: 388.74 },
  { year: "2021", meanScore: 393.69 },
  { year: "2022", meanScore: 380.0 },
  { year: "2023", meanScore: 394.71 },
];

// Data from spreadsheet rows 58-67 (Subjects/Learning Areas Analysis)
const kjseaSubjectsData = [
  { grade: "EE1", count: 18 },
  { grade: "EE2", count: 16 },
  { grade: "ME1", count: 10 },
  { grade: "ME2", count: 4 },
  { grade: "AE1", count: 0 },
  { grade: "AE2", count: 0 },
  { grade: "BE1", count: 0 },
  { grade: "BE2", count: 0 },
];

// Data from spreadsheet rows 69-70 (General Points Analysis)
const kjseaPointsData = [
  { grade: "EE1", students: 28 },
  { grade: "EE2", students: 14 },
  { grade: "ME1", students: 6 },
  { grade: "ME2", students: 0 },
  { grade: "AE1", students: 0 },
  { grade: "AE2", students: 0 },
  { grade: "BE1", students: 0 },
  { grade: "BE2", students: 0 },
];

const kjsea2025Data = [
  { subject: "English", points: 7.0 },
  { subject: "Kiswahili", points: 6.63 },
  { subject: "Mathematics", points: 7.0 },
  { subject: "Science", points: 7.25 },
  { subject: "Agriculture", points: 7.29 },
  { subject: "SST", points: 7.35 },
  { subject: "CRE", points: 7.48 },
  { subject: "CAS", points: 6.85 },
  { subject: "Pre-Tech", points: 6.23 },
];

const highlights = [
  {
    icon: Trophy,
    value: "394.71",
    label: "2023 KCPE Mean Score",
    color: "from-red-500 to-red-600",
  },
  {
    icon: TrendingUp,
    value: "+43.45",
    label: "Points Growth (2010-2023)",
    color: "from-blue-600 to-blue-700",
  },
  {
    icon: Award,
    value: "100%",
    label: "Transition Rate",
    color: "from-blue-800 to-blue-900",
  },
  {
    icon: Target,
    value: "Top 5",
    label: "County Ranking",
    color: "from-red-600 to-red-700",
  },
];

export function Performance() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20"
          >
            Academic Excellence
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
          >
            Our Performance
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white/80 max-w-2xl mx-auto"
          >
            A track record of consistent academic excellence and continuous
            improvement over the years.
          </motion.p>
        </div>
      </section>

      {/* Stats Highlights */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {highlights.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div
                  className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}
                >
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-1">
                  {stat.value}
                </div>
                <div className="text-slate-600 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KJSEA 2025 Results - Now at Top */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              KJSEA 2025 Performance
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Grade 9 Examination Results
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Detailed breakdown of student performance in KJSEA 2025
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* KJSEA Subject Performance */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-slate-100"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Subject Performance
                </h3>
                <p className="text-slate-600 text-sm">
                  Average Points by Subject (Grade 9)
                </p>
                <p className="text-blue-600 font-semibold mt-2">
                  School Average Points: 6.23
                </p>
              </div>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kjsea2025Data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="subject" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis domain={[0, 8]} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="points"
                      fill="#2563eb"
                      radius={[6, 6, 0, 0]}
                      name="Points"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* KJSEA Learning Areas Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-slate-100"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Learning Areas Analysis
                </h3>
                <p className="text-slate-600 text-sm">
                  Number of Units per Grade
                </p>
                <p className="text-blue-600 font-semibold mt-2">
                  Total Units Examined: 432
                </p>
              </div>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kjseaSubjectsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="grade" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="count"
                      fill="#3b82f6"
                      radius={[6, 6, 0, 0]}
                      name="Number of Units"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* KJSEA General Points Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-slate-100"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  General Points Analysis
                </h3>
                <p className="text-slate-600 text-sm">
                  Number of Students per Grade
                </p>
                <p className="text-blue-600 font-semibold mt-2">
                  Total Students: 48
                </p>
              </div>
              <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kjseaPointsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="grade" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="students"
                      fill="#1e40af"
                      radius={[6, 6, 0, 0]}
                      name="Number of Students"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Performance Graph */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
              Performance Trends
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              KCPE Mean Score Progress (2010-2023)
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl p-6 md:p-10 shadow-lg border border-slate-100"
          >
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis domain={[300, 410]} />
                  <Tooltip />
                  <Bar
                    dataKey="meanScore"
                    fill="#dc2626"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Performance Table */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
              Detailed Results
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              KCPE Year-by-Year Performance
            </h2>
          </motion.div>

          <div className="overflow-x-auto bg-white rounded-3xl shadow-lg border border-slate-100">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-red-600 to-red-700">
                  {performanceData.map((d) => (
                    <th
                      key={d.year}
                      className="px-4 py-4 text-sm text-white font-semibold text-center"
                    >
                      {d.year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {performanceData.map((d) => (
                    <td
                      key={d.year}
                      className="px-4 py-4 text-center text-slate-700 font-medium"
                    >
                      {d.meanScore}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}